// Open from the user's click, before React rendering or canvas generation.
export function openTicketPrintWindow(): Window {
  const popup = window.open(
    "about:blank",
    "_blank",
    "top=40,left=40,width=1000,height=700,resizable=yes,scrollbars=yes"
  );
  if (!popup) throw new Error("Ticket print popup blocked");
  popup.document.title = "EasyBar";
  return popup;
}

export function waitForTicketImages(element: HTMLElement, timeoutMs = 15000): Promise<void> {
  return Promise.all(Array.from(element.querySelectorAll("img")).map(image =>
    new Promise<void>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout>;
      const finish = () => {
        clearTimeout(timer);
        image.removeEventListener("load", finish);
        image.removeEventListener("error", finish);
        if (image.complete && image.naturalWidth > 0) resolve();
        else reject(new Error("Ticket image failed to load"));
      };
      if (image.complete) {
        finish();
      } else {
        image.addEventListener("load", finish);
        image.addEventListener("error", finish);
        timer = setTimeout(finish, timeoutMs);
      }
    })
  )).then(() => undefined);
}

export async function printTicketCanvases(popup: Window, canvases: HTMLCanvasElement[]) {
  if (popup.closed || !canvases.length) throw new Error("Ticket print window unavailable");
  const doc = popup.document;
  const style = doc.createElement("style");
  style.textContent = `
    @page { margin: 0; padding: 0; size: A4 landscape; }
    body { width: 100%; height: 100%; margin: 0 auto; }
    .ticket-page { break-after: page; page-break-after: always; }
    .ticket-page:last-child { break-after: auto; page-break-after: auto; }
    img { display: block; width: 100%; height: auto; }
  `;
  doc.head.appendChild(style);
  doc.body.textContent = "";
  for (const canvas of canvases) {
    const page = doc.createElement("div");
    page.className = "ticket-page";
    const image = doc.createElement("img");
    image.src = canvas.toDataURL();
    page.appendChild(image);
    doc.body.appendChild(page);
  }
  await waitForTicketImages(doc.body);
  if (popup.closed) throw new Error("Ticket print window closed");
  // Chromium may return from print() before its dialog closes. Only afterprint
  // indicates that the dialog has finished (including cancellation).
  popup.addEventListener("afterprint", () => popup.close(), { once: true });
  popup.focus();
  popup.print();
}
